/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo } from 'react';
import { icons, TRIconType } from '@tapresearch/ui/components';
import { useDisclosure } from '@tapresearch/ui/hooks';
import { ActivatePlayerInsightsModal } from './ActivatePlayerInsightsModal';
import { SUPPLIER_STATUS_ACTIVE } from '../../supplier_dashboard/util/constants';

interface SidebarNavProps {
  user: Record<string, any>;
  constants: Record<string, any>;
  onNavigate: (path: string, internal?: boolean) => void;
  isLinkActive?: {
    dashboard?: boolean;
    appTitles?: boolean;
    surveys?: boolean;
    documentation?: boolean;
  };
  internal?: 'supplier_dashboard' | 'insights_platform';
  piActivation?: {
    onOpen?: () => void;
  };
  isInactivePublisher?: boolean;
}

interface NavItem {
  order: number;
  icon: TRIconType;
  label: string;
  active: boolean;
  onClick: () => void;
}
export const useSidebarNav = (props: SidebarNavProps) =>
  useMemo(() => {
    const {
      constants,
      user,
      onNavigate,
      isLinkActive,
      internal,
      piActivation,
      isInactivePublisher = false,
    } = props;
    const { is_active } = (user && user.organization_membership) || {};
    const { supplier, customer } = (user && user.organization) || {};
    const isSupplierDashboard = internal === 'supplier_dashboard';
    const isInsightsPlatform = internal === 'insights_platform';
    const playerInsightsEnabled = !!user.organization?.can_developer_survey;
    const isBannedCustomer =
      customer &&
      customer.insights_moderation_status ===
        constants.INSIGHTS_MODERATION_STATUS_BANNED;

    const hasAccess = !!is_active && !isBannedCustomer;
    const isSupplier = !!supplier;
    const isCustomer = !!customer;

    const showPlayerInsightsActivation =
      !!hasAccess &&
      !isInactivePublisher &&
      !!piActivation?.onOpen &&
      isSupplier &&
      !isCustomer &&
      !playerInsightsEnabled;

    const supplierRoutePrefix = isSupplierDashboard
      ? ''
      : '/supplier_dashboard';
    const insightsRoutePrefix = isInsightsPlatform ? '' : '/insights_platform';

    if (!hasAccess) return [];

    const hasSurveysAccess =
      !isInactivePublisher &&
      (!!showPlayerInsightsActivation || (isCustomer && !isBannedCustomer));

    const itemsArr: NavItem[] = [];

    const nav = {
      dashboard: {
        order: 1,
        icon: icons.tr.DashboardIcon,
        label: 'Dashboard',
        active: !!isLinkActive?.dashboard,
        onClick: () =>
          onNavigate(`${supplierRoutePrefix}/`, isSupplierDashboard),
      },
      appTitles: {
        order: 2,
        icon: icons.io5.Apps,
        label: 'App titles',
        active: !!isLinkActive?.appTitles,
        onClick: () =>
          onNavigate(`${supplierRoutePrefix}/overview`, isSupplierDashboard),
      },
      documentation: {
        order: 4,
        icon: icons.fi.Book,
        label: 'Documentation',
        active: !!isLinkActive?.documentation,
        onClick: () =>
          onNavigate(
            `${supplierRoutePrefix}/documentation`,
            isSupplierDashboard
          ),
      },
      surveys: {
        order: 3,
        icon: isSupplier ? icons.md.PersonSearch : icons.fi.Clipboard,
        label: isSupplier ? 'Player Insights' : 'Surveys',
        active: !!isLinkActive?.surveys,
        onClick: () =>
          !!showPlayerInsightsActivation
            ? piActivation.onOpen()
            : onNavigate(`${insightsRoutePrefix}/`, isInsightsPlatform),
      },
    } as const;

    if (isSupplier) {
      if (!isInactivePublisher) itemsArr.push(nav.dashboard, nav.appTitles);

      itemsArr.push(nav.documentation);
    }

    if (!!hasSurveysAccess) itemsArr.push(nav.surveys);

    return itemsArr.sort((a, b) => a.order - b.order);
  }, [props]);

export const usePlayerInsightsActivation = ({
  user,
  isInactivePublisher = false,
}: {
  user: any;
  isInactivePublisher?: boolean;
}): null | {
  onOpen: () => void;
  modal: JSX.Element;
} => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const isAdmin = user.organization_membership?.is_admin;
  if (
    !user ||
    !user.organization?.supplier ||
    !!user.organization?.customer ||
    !!user.organization?.can_developer_survey ||
    !user.organization_membership?.is_active ||
    isInactivePublisher
  )
    return null;

  const modal = (
    <ActivatePlayerInsightsModal
      redirectLink="/insights_platform/dashboard"
      isOpen={isOpen}
      onClose={onClose}
      isAdmin={isAdmin}
      orgIdentifier={user.organization.identifier}
    />
  );
  return {
    onOpen,
    modal,
  };
};

export const useInactivePublisher = (user: any) =>
  useMemo(() => {
    const { organization, is_active } = user || {};
    const { supplier } = organization || {};
    if (!supplier) return false;

    const status = supplier.status;
    return status != SUPPLIER_STATUS_ACTIVE || !is_active;
  }, [user]);
